/**
 * Background / black-border removal for raster tiles (ArcGIS "Display
 * background value" + "NoData colour", made robust for a web tile pipeline).
 *
 * TiTiler's `nodata=` masks only pixels whose value is *exactly* the sentinel.
 * A COG's black collar is exactly 0 at full resolution, but reprojection into
 * the Web-Mercator tile grid — and GDAL's averaged overviews — blend that 0
 * with real data, so the outermost pixels arrive as 3 / 7 / 11 … and survive
 * the mask. That residue is the dashed fringe left around a "masked" scene.
 *
 * The fix is a tolerance, which TiTiler has no parameter for. So the tile is
 * post-processed in display space: any pixel whose channels all sit within
 * `tolerance` of the key level is dropped — or repainted, when the operator
 * picks a border colour instead of transparency. Server-side `nodata` still
 * runs first and does the bulk of the work; this cleans the edge and owns the
 * colour.
 *
 * Engine hookup mirrors `shadedReliefBlend.ts` — a MapLibre protocol and an
 * OpenLayers `tileLoadFunction`, both driven by markers on the tile URL, both
 * exchanging decoded pixels rather than re-encoded PNGs.
 */

import { isShadedReliefTileUrl } from '@/components/raster/applyRasterToolCard';
import {
  GV_BACKGROUND_FILL_PARAM,
  GV_BACKGROUND_LEVEL_PARAM,
  GV_BACKGROUND_MASK_PARAM,
  GV_BACKGROUND_TOLERANCE_PARAM,
} from '@/components/raster/rasterDra';
import {
  failOpenLayersTile,
  fetchShadedReliefBlendedImageData,
  setOpenLayersTilePixels,
  wrapShadedReliefMapLibreTemplate,
  type OpenLayersImageTile,
} from '@/components/raster/shadedReliefBlend';
import {
  fetchTileImage,
  fillTilePlaceholders,
  imageDataToEngineImage,
  readTileParam,
  runTileTask,
  throwIfAborted,
  tileImageToImageData,
} from '@/components/raster/tilePixelPipeline';

export const BACKGROUND_MASK_PROTOCOL = 'gvbgmask';

/** RGB triple, 0–255 per channel. */
export type BackgroundFill = readonly [number, number, number];

export interface BackgroundKey {
  /** Display level (0–255) treated as background. */
  readonly level: number;
  /** How far from {@link level} a channel may sit and still count as background. */
  readonly tolerance: number;
  /**
   * Colour painted over background and no-data pixels. `null` keeps ArcGIS's
   * "No colour" default — the pixels become fully transparent.
   */
  readonly fill: BackgroundFill | null;
}

/** True when the template asks for client-side background handling. */
export function isBackgroundMaskTileUrl(tilesUrl: string): boolean {
  return readTileParam(tilesUrl, GV_BACKGROUND_MASK_PARAM) === '1';
}

function clampChannel(v: number): number {
  return Math.max(0, Math.min(255, v));
}

/** Parse the `gv_bg_fill=rrggbb` marker. Returns null for absent / malformed. */
function parseFillParam(raw: string | null): BackgroundFill | null {
  if (!raw) return null;
  const cleaned = raw.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{6}$/.test(cleaned)) return null;
  const n = Number.parseInt(cleaned, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/**
 * Read the key level, tolerance and fill colour off a tile template.
 *
 * Returns null when there is nothing for the client to do — no tolerance to
 * widen the mask with and no colour to paint — because server-side `nodata`
 * already covers that case for free.
 */
export function readBackgroundKey(tilesUrl: string): BackgroundKey | null {
  if (!isBackgroundMaskTileUrl(tilesUrl)) return null;
  const level = Number(readTileParam(tilesUrl, GV_BACKGROUND_LEVEL_PARAM) ?? '0');
  const rawTolerance = Number(
    readTileParam(tilesUrl, GV_BACKGROUND_TOLERANCE_PARAM) ?? '0',
  );
  const tolerance = Number.isFinite(rawTolerance)
    ? Math.max(0, Math.min(255, rawTolerance))
    : 0;
  const fill = parseFillParam(readTileParam(tilesUrl, GV_BACKGROUND_FILL_PARAM));
  if (tolerance <= 0 && !fill) return null;
  return {
    level: Number.isFinite(level) ? clampChannel(level) : 0,
    tolerance,
    fill,
  };
}

function hasOpaquePixel(pixels: Uint8ClampedArray): boolean {
  for (let i = 3; i < pixels.length; i += 4) {
    if (pixels[i] !== 0) return true;
  }
  return false;
}

/**
 * Drop — or repaint — every pixel that reads as background. Mutates and returns
 * `pixels` (RGBA, row-major).
 *
 * Requiring *all three* channels to sit within `tolerance` of the key level
 * keeps coloured terrain intact; only neutral near-background pixels (the
 * collar) are caught. When a fill colour is set, pixels the server already
 * masked to transparent are painted too, which is what makes the border colour
 * cover the whole no-data region rather than just the resampled fringe.
 *
 * A tile with no opaque pixel at all lies outside the raster footprint, so it
 * is left alone — otherwise a fill colour would tile solid blocks across the
 * rest of the map.
 */
export function maskBackgroundPixels(
  pixels: Uint8ClampedArray,
  key: BackgroundKey,
): Uint8ClampedArray {
  const { fill } = key;
  if (fill && !hasOpaquePixel(pixels)) return pixels;

  const lo = clampChannel(key.level - key.tolerance);
  const hi = clampChannel(key.level + key.tolerance);

  for (let i = 0; i < pixels.length; i += 4) {
    if (pixels[i + 3] === 0) {
      if (fill) paint(pixels, i, fill);
      continue;
    }
    const r = pixels[i];
    const g = pixels[i + 1];
    const b = pixels[i + 2];
    if (r >= lo && r <= hi && g >= lo && g <= hi && b >= lo && b <= hi) {
      if (fill) paint(pixels, i, fill);
      else pixels[i + 3] = 0;
    }
  }
  return pixels;
}

function paint(pixels: Uint8ClampedArray, i: number, fill: BackgroundFill): void {
  pixels[i] = fill[0];
  pixels[i + 1] = fill[1];
  pixels[i + 2] = fill[2];
  pixels[i + 3] = 255;
}

/**
 * Fetch one tile and apply the background key. Shaded-relief templates are
 * blended first so the two effects compose in pixel space instead of each
 * re-encoding the tile for the other.
 */
export async function fetchBackgroundMaskedImageData(
  tileUrl: string,
  signal?: AbortSignal | null,
): Promise<ImageData> {
  const data = isShadedReliefTileUrl(tileUrl)
    ? await fetchShadedReliefBlendedImageData(tileUrl, signal)
    : tileImageToImageData(await fetchTileImage(tileUrl, signal));
  throwIfAborted(signal);
  const key = readBackgroundKey(tileUrl);
  if (key) maskBackgroundPixels(data.data, key);
  return data;
}

/** Wrap a background-keyed template so MapLibre fetches through our protocol. */
export function wrapBackgroundMaskMapLibreTemplate(template: string): string {
  if (!readBackgroundKey(template)) return template;
  return `${BACKGROUND_MASK_PROTOCOL}://{z}/{x}/{y}?t=${encodeURIComponent(template)}`;
}

/**
 * Single entry point for MapLibre template wrapping. Background keying wins
 * because its handler blends shaded relief internally; templates needing
 * neither pass through untouched.
 */
export function wrapRasterTileTemplateForMapLibre(template: string): string {
  if (readBackgroundKey(template)) {
    return wrapBackgroundMaskMapLibreTemplate(template);
  }
  return wrapShadedReliefMapLibreTemplate(template);
}

type MapLibreProtocol = {
  addProtocol: (
    name: string,
    handler: (
      request: { url: string },
      abortController?: AbortController,
    ) => Promise<{ data: ImageBitmap | ArrayBuffer }>,
  ) => void;
};

let backgroundMaskProtocolRegistered = false;

/** Register the `gvbgmask://` protocol on MapLibre once per process. */
export function ensureBackgroundMaskProtocol(maplibre: MapLibreProtocol): void {
  if (backgroundMaskProtocolRegistered) return;
  maplibre.addProtocol(BACKGROUND_MASK_PROTOCOL, async (request, abortController) => {
    const raw = request.url;
    const pathMatch = /gvbgmask:\/\/(?:[^/]+\/)?(\d+)\/(\d+)\/(\d+)/i.exec(raw);
    const tMatch = /[?&]t=([^&]+)/.exec(raw);
    if (!pathMatch || !tMatch) throw new Error(`Bad background-mask URL: ${raw}`);
    const [, z, x, y] = pathMatch;
    const template = decodeURIComponent(tMatch[1]);
    const filled = fillTilePlaceholders(template, z, x, y);
    const signal = abortController?.signal ?? null;
    const data = await runTileTask(async () =>
      imageDataToEngineImage(await fetchBackgroundMaskedImageData(filled, signal)),
    );
    return { data };
  });
  backgroundMaskProtocolRegistered = true;
}

/**
 * OpenLayers `tileLoadFunction` that keys out the background collar. No-op
 * when the template carries no background markers, so the shaded-relief
 * loader attached before it keeps ownership of plain templates.
 */
export function attachBackgroundMaskTileLoader(source: unknown, templateUrl: string): void {
  if (!readBackgroundKey(templateUrl)) return;
  const olSource = source as {
    setTileLoadFunction?: (
      fn: (tile: OpenLayersImageTile, url: string) => void,
    ) => void;
  } | null;
  if (typeof olSource?.setTileLoadFunction !== 'function') return;

  olSource.setTileLoadFunction((tile, src) => {
    void runTileTask(() => fetchBackgroundMaskedImageData(src))
      .then((data) => setOpenLayersTilePixels(tile, data))
      .catch(() => failOpenLayersTile(tile));
  });
}
